import { IAlerta } from 'modelosSeguridad/src';
import { ISirena } from 'modelos/src';
import { CoordinatesHelper } from './coordinates.helper';

/**
 * Resolución geográfica de la sirena que sonó por una alerta.
 *
 * En seguridad NO existe vínculo por referencia entre la alerta y la sirena:
 * ni `IAlerta` ni `IEventoSirena` guardan el id de la otra punta. El comando
 * de encendido viaja identificando vecino, cliente y control, nunca alerta.
 * La sirena se elige en el instante del disparo por cercanía a la ubicación,
 * en `seguridad-boton-nest/src/entidades/sirenas/sirenas.service.ts`
 * (`sirenaMasCercana`), y ese dato no queda persistido en ningún lado.
 *
 * Acá se replica esa misma regla contra el espejo de sirenas de Irix para
 * poder mostrar la cámara vinculada a la sirena. Es una reconstrucción, no una
 * referencia.
 *
 * Se replica en dos pasos, igual que seguridad: el prefiltro geográfico de
 * 500 m va en Mongo (`filtroSirenasCercanas`) y la regla real —distancia
 * medida contra el radio de acción de cada sirena— en memoria
 * (`sirenaMasCercanaAAlerta`).
 *
 * Diferencias deliberadas con seguridad, y por qué:
 *
 * - Seguridad exige `online: true`. Ese estado es el de AHORA, no el del
 *   momento de la alerta: filtrarlo acá descartaría justamente la sirena
 *   correcta si desde entonces se cayó. Se pide solo `activa`.
 *
 * - El radio de acción en seguridad es
 *   `sirena.configs.distanciaCobertura || cliente.configuracion.distanciaCobertura || 200`.
 *   El espejo de Irix no sincroniza `configs` ni la config del cliente de
 *   seguridad, así que acá solo se puede usar el default de 200 m. Si un cliente tiene
 *   una cobertura distinta configurada en seguridad, este cálculo difiere y
 *   hay que sincronizar `distanciaCobertura` para cerrar la brecha.
 */
export const RADIO_ACCION_SIRENA_M = 200;

/**
 * Radio del prefiltro en Mongo, igual que seguridad.
 */
export const RADIO_PREFILTRO_SIRENA_M = 500;

/**
 * Radio terrestre que usa `$centerSphere` para pasar metros a radianes.
 *
 * Seguridad calcula el prefiltro con el radio ECUATORIAL (`0.5 / 6378.1` km)
 * y la distancia final con el radio MEDIO (6371 km, en `distanciaEnMetros`).
 * Es una inconsistencia suya; se replica cada uno donde corresponde para que
 * los dos pasos den exactamente lo mismo que allá.
 */
const RADIO_TERRESTRE_ECUATORIAL_M = 6_378_100;

/**
 * Filtro de sirenas candidatas para `/sirenas`: activas y dentro del radio de
 * prefiltro alrededor de la alerta.
 */
export function filtroSirenasCercanas(
  punto: [number, number],
): Record<string, unknown> {
  return {
    activa: true,
    ubicacion: {
      $geoWithin: {
        $centerSphere: [
          punto,
          RADIO_PREFILTRO_SIRENA_M / RADIO_TERRESTRE_ECUATORIAL_M,
        ],
      },
    },
  };
}

export interface SirenaResuelta {
  sirena: ISirena;
  distancia: number; // Distancia en metros entre la alerta y la sirena.
}

/** Punto de la alerta en formato GeoJSON `[lng, lat]`. */
export function coordenadasDeAlerta(alerta?: IAlerta): [number, number] | null {
  const geo = alerta?.geojson?.coordinates;
  if (geo?.length === 2) {
    return [geo[0], geo[1]];
  }
  const lat = alerta?.ubicacion?.lat;
  const lng = alerta?.ubicacion?.lng;
  if (typeof lat === 'number' && typeof lng === 'number') {
    return [lng, lat];
  }
  return null;
}

/**
 * Sirena más cercana a la alerta que la tenga dentro de su radio de acción.
 * `undefined` si ninguna califica — mismo desenlace que en seguridad, donde
 * `sirenaMasCercana` tira `NotFoundException` y no suena nada.
 */
export function sirenaMasCercanaAAlerta(
  alerta: IAlerta | undefined,
  sirenas: ISirena[],
): SirenaResuelta | undefined {
  const puntoAlerta = coordenadasDeAlerta(alerta);
  if (!puntoAlerta) return undefined;

  let resuelta: SirenaResuelta | undefined;
  for (const sirena of sirenas) {
    if (sirena.activa === false) continue;
    const coords = sirena.ubicacion?.coordinates;
    if (coords?.length !== 2) continue;
    if (typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
      continue;
    }

    const distancia = CoordinatesHelper.getDistance(puntoAlerta, [
      coords[0],
      coords[1],
    ]);
    if (distancia > RADIO_ACCION_SIRENA_M) continue;
    if (!resuelta || distancia < resuelta.distancia) {
      resuelta = { sirena, distancia };
    }
  }
  return resuelta;
}
